import { Ray, Vec3 } from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';
import { createSceneObject, SceneObject, SceneObjectDef } from './scene-object';
import { createWater, Water } from './water';

type DropTarget = { kind: 'image', src: string } |
    { kind: 'text', text: string } |
    { kind: 'shape', shape: 'box' | 'sphere' | 'plane' } |
    { kind: 'water' };

// Simam brand purple as a default object colour
const BRAND: [number, number, number] = [0.486, 0.227, 0.929];

// undoable add for a content object. move() must run AFTER scene.add() (it
// needs `element.scene` to already be set, to update world bounds), so this
// can't reuse the generic AddElementOp — do() sequences add-then-move.
class AddContentObjectOp {
    name = 'addContentObject';
    scene: Scene;
    element: SceneObject | Water;
    position: Vec3;

    constructor(scene: Scene, element: SceneObject | Water, position: Vec3) {
        this.scene = scene;
        this.element = element;
        this.position = position;
    }

    async do() {
        await this.scene.add(this.element);
        this.element.move(this.position);
    }

    undo() {
        this.scene.remove(this.element);
    }

    destroy() {
        this.element.destroy?.();
    }
}

const registerContentEvents = (events: Events, scene: Scene) => {
    // place new content near the scene centre (falls back to the origin)
    const placeAt = () => {
        const b = scene.bound;
        if (b && Number.isFinite(b.center.x) && Number.isFinite(b.center.y) && Number.isFinite(b.center.z)) {
            return b.center.clone();
        }
        return new Vec3(0, 0, 0);
    };

    // ray-cast from a screen position onto the y=0 (or scene-centre-height)
    // ground plane, used to place content dropped at a specific viewport point
    const dropPosition = (screenX: number, screenY: number) => {
        const ray = new Ray();
        scene.camera.getRay(screenX, screenY, ray);

        const planeY = placeAt().y;
        const denom = ray.direction.y;
        if (Math.abs(denom) < 1e-6) {
            return placeAt();
        }

        const t = (planeY - ray.origin.y) / denom;
        if (t < 0 || !Number.isFinite(t)) {
            return placeAt();
        }

        return new Vec3(
            ray.origin.x + ray.direction.x * t,
            ray.origin.y + ray.direction.y * t,
            ray.origin.z + ray.direction.z * t
        );
    };

    // adding content is undoable: accidentally clicking Add Shape/Text/Image can be undone.
    const addObject = async (def: SceneObjectDef, name: string, pos?: Vec3) => {
        const position = pos ?? placeAt();
        const obj = await createSceneObject(scene, def, name);
        events.fire('edit.add', new AddContentObjectOp(scene, obj, position));
        return obj;
    };

    const addWater = (pos?: Vec3) => {
        const position = pos ?? placeAt();
        const water = createWater(scene, 'Water');
        events.fire('edit.add', new AddContentObjectOp(scene, water, position));
        return water;
    };

    events.on('content.addImage', (src: string) => {
        addObject({ kind: 'image', src, aspect: 1, billboard: false }, 'Image');
    });

    events.on('content.addText', (text: string) => {
        const value = text || 'Text';
        addObject({
            kind: 'text',
            text: value,
            color: '#ffffff',
            bgColor: '#7C3AED',
            fontSize: 64,
            billboard: true
        }, value);
    });

    events.on('content.addShape', (shape: 'box' | 'sphere' | 'plane') => {
        const name = shape.charAt(0).toUpperCase() + shape.slice(1);
        addObject({ kind: 'shape', shape, color: BRAND }, name);
    });

    events.on('content.addWater', () => {
        addWater();
    });

    // content dragged from the scene panel and dropped at a specific viewport
    // position - same undoable add path, just placed at the drop point
    // instead of the scene centre
    events.on('content.dropAt', (target: DropTarget, screenX: number, screenY: number) => {
        const pos = dropPosition(screenX, screenY);
        switch (target.kind) {
            case 'image':
                addObject({ kind: 'image', src: target.src, aspect: 1, billboard: false }, 'Image', pos);
                break;
            case 'text':
                addObject({
                    kind: 'text',
                    text: target.text,
                    color: '#ffffff',
                    bgColor: '#7C3AED',
                    fontSize: 64,
                    billboard: true
                }, target.text, pos);
                break;
            case 'shape': {
                const name = target.shape.charAt(0).toUpperCase() + target.shape.slice(1);
                addObject({ kind: 'shape', shape: target.shape, color: BRAND }, name, pos);
                break;
            }
            case 'water':
                addWater(pos);
                break;
        }
    });
};

export { registerContentEvents };
